import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, ShieldAlert, Upload, X } from "lucide-react";

const inputCls = "bg-white border-steel text-charcoal h-11";
const QTYPE_LABELS = {
  multiple_choice: "Multiple choice", true_false: "True / False", scenario: "Scenario judgement",
  hazard: "Hazard recognition", multiple_select: "Select all that apply", numerical: "Numerical",
};
const lines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
const paras = (s) => s.split(/\n\s*\n/).map((x) => x.trim()).filter(Boolean);

function QEditor({ q, onChange, onDelete, categories, testid }) {
  const set = (k, v) => onChange({ ...q, [k]: v });
  const qtype = q.qtype || "multiple_choice";
  const opts = (q.options || []).join("\n");
  const needsOptions = !["true_false", "numerical"].includes(qtype);
  return (
    <div className="bg-elevated border border-steel rounded-md p-4 space-y-3" data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <Textarea value={q.question} onChange={(e) => set("question", e.target.value)} placeholder="Question text"
          className="bg-white border-steel text-charcoal min-h-16" data-testid={`${testid}-question`} />
        <Button size="sm" variant="outline" onClick={onDelete} className="border-red-300 text-red-600 bg-white shrink-0" data-testid={`${testid}-delete`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <select value={qtype} onChange={(e) => set("qtype", e.target.value)} className="h-11 rounded-md border border-steel bg-white px-2 text-sm text-charcoal" data-testid={`${testid}-type`}>
          {Object.entries(QTYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {categories && (
          <select value={q.category} onChange={(e) => set("category", e.target.value)} className="h-11 rounded-md border border-steel bg-white px-2 text-sm text-charcoal" data-testid={`${testid}-category`}>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <label className="flex items-center gap-2 text-sm text-charcoal cursor-pointer" data-testid={`${testid}-sc`}>
          <Checkbox checked={!!q.safety_critical} onCheckedChange={(c) => set("safety_critical", !!c)} />
          <span className="flex items-center gap-1 text-red-600 font-semibold"><ShieldAlert className="h-3.5 w-3.5" /> Safety-critical</span>
        </label>
      </div>
      {needsOptions && (
        <Textarea value={opts} onChange={(e) => set("options", lines(e.target.value))} placeholder="Options — one per line"
          className="bg-white border-steel text-charcoal min-h-20 font-mono text-xs" data-testid={`${testid}-options`} />
      )}
      {qtype === "multiple_select" ? (
        <Input value={(q.correct_answer || []).join(" | ")} onChange={(e) => set("correct_answer", e.target.value.split("|").map((x) => x.trim()).filter(Boolean))}
          placeholder="Correct options, separated by | (must match options exactly)" className={inputCls} data-testid={`${testid}-correct`} />
      ) : qtype === "true_false" ? (
        <select value={q.correct_answer || "True"} onChange={(e) => set("correct_answer", e.target.value)} className="h-11 rounded-md border border-steel bg-white px-2 text-sm text-charcoal" data-testid={`${testid}-correct`}>
          <option>True</option><option>False</option>
        </select>
      ) : needsOptions && (q.options || []).length ? (
        <select value={q.correct_answer || ""} onChange={(e) => set("correct_answer", e.target.value)} className="h-11 rounded-md border border-steel bg-white px-2 text-sm text-charcoal" data-testid={`${testid}-correct`}>
          <option value="">— select correct option —</option>
          {(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <Input value={q.correct_answer || ""} onChange={(e) => set("correct_answer", e.target.value)} placeholder="Correct answer"
          className={inputCls} data-testid={`${testid}-correct`} />
      )}
      <Input value={q.explanation || ""} onChange={(e) => set("explanation", e.target.value)} placeholder="Explanation shown after submission (trainee learns from mistakes)"
        className={inputCls} data-testid={`${testid}-explanation`} />
    </div>
  );
}

function ModuleEditor({ module: m, onSaved }) {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [imgFile, setImgFile] = useState(null);

  useEffect(() => {
    setF({
      title: m.title, pass_mark: m.pass_mark ?? 80, video_url: m.video_url || "",
      objectives: (m.objectives || []).join("\n"), theory: (m.theory || []).join("\n\n"),
      safety_messages: (m.safety_messages || []).join("\n"),
      practical_exercises: (m.practical_exercises || []).join("\n"),
      instructor_criteria: (m.instructor_criteria || []).join("\n"),
      quiz: JSON.parse(JSON.stringify(m.quiz || [])), images: m.images || [],
    });
  }, [m]);

  if (!f) return null;
  const set = (k, v) => setF({ ...f, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/training/modules/${m.day}`, {
        title: f.title, pass_mark: parseInt(f.pass_mark, 10) || 80, video_url: f.video_url,
        objectives: lines(f.objectives), theory: paras(f.theory),
        safety_messages: lines(f.safety_messages), practical_exercises: lines(f.practical_exercises),
        instructor_criteria: lines(f.instructor_criteria), quiz: f.quiz, images: f.images,
      });
      toast.success(`Day ${m.day} module saved.`);
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const uploadImage = async () => {
    if (!imgFile) return;
    const fd = new FormData();
    fd.append("file", imgFile);
    try {
      const r = await api.post(`/admin/training/modules/${m.day}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("images", [...f.images, r.data.url]);
      setImgFile(null);
      toast.success("Image added — save the module to keep it.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const removeImage = async (url) => {
    set("images", f.images.filter((u) => u !== url));
    try { await api.post(`/admin/training/modules/${m.day}/image/remove`, { url }); } catch { /* saved on PUT anyway */ }
  };

  const field = (label, key, hint, isPara = false) => (
    <div className="space-y-1.5" data-testid={`field-${key}`}>
      <label className="text-sm font-semibold text-charcoal">{label}</label>
      <Textarea value={f[key]} onChange={(e) => set(key, e.target.value)} placeholder={hint}
        className="bg-white border-steel text-charcoal min-h-28" data-testid={`module-${key}`} />
      <p className="text-[10px] text-slate-400 font-mono">{isPara ? "Separate paragraphs with a blank line" : "One per line"}</p>
    </div>
  );

  return (
    <div className="space-y-5" data-testid={`module-editor-${m.day}`}>
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="space-y-1.5 sm:col-span-2">
          <label className="text-sm font-semibold text-charcoal">Module title</label>
          <Input value={f.title} onChange={(e) => set("title", e.target.value)} className={inputCls} data-testid="module-title" />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-semibold text-charcoal">Quiz pass mark %</label>
          <Input type="number" min="1" max="100" value={f.pass_mark} onChange={(e) => set("pass_mark", e.target.value)} className={inputCls} data-testid="module-pass-mark" />
        </div>
      </div>
      {field("Learning objectives", "objectives", "One objective per line")}
      {field("Theory lesson content", "theory", "Write the lesson. Blank line between paragraphs.", true)}
      <div className="space-y-1.5" data-testid="field-images">
        <label className="text-sm font-semibold text-charcoal">Images</label>
        <div className="flex flex-wrap gap-2">
          {f.images.map((url) => (
            <div key={url} className="relative">
              <img src={fileUrl(url)} alt="" className="h-20 w-32 object-cover rounded-md border border-steel" />
              <button type="button" onClick={() => removeImage(url)} className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full h-5 w-5 flex items-center justify-center" data-testid="module-image-remove"><X className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input type="file" accept="image/*" onChange={(e) => setImgFile(e.target.files?.[0] || null)} className="bg-white border-steel text-charcoal" data-testid="module-image-input" />
          <Button size="sm" variant="outline" onClick={uploadImage} className="border-steel text-charcoal bg-white shrink-0" data-testid="module-image-upload"><Upload className="h-4 w-4 mr-1" /> Add</Button>
        </div>
      </div>
      <div className="space-y-1.5" data-testid="field-video">
        <label className="text-sm font-semibold text-charcoal">Video URL (optional)</label>
        <Input value={f.video_url} onChange={(e) => set("video_url", e.target.value)} placeholder="https://... or /api/uploads/....mp4" className={inputCls} data-testid="module-video" />
      </div>
      {field("Key safety messages", "safety_messages", "One per line")}
      {field("Practical exercises", "practical_exercises", "One per line")}
      {field("Instructor criteria", "instructor_criteria", "One per line")}

      <div className="space-y-3" data-testid="quiz-editor">
        <div className="flex items-center justify-between">
          <label className="text-sm font-semibold text-charcoal">Quiz questions ({f.quiz.length})</label>
          <Button size="sm" variant="outline" data-testid="quiz-add-btn"
            onClick={() => set("quiz", [...f.quiz, { id: `new-${Date.now()}`, qtype: "multiple_choice", question: "", options: [], correct_answer: "", explanation: "", safety_critical: false, points: 1 }])}
            className="border-steel text-charcoal bg-white"><Plus className="h-4 w-4 mr-1" /> Add Question</Button>
        </div>
        {f.quiz.map((q, i) => (
          <QEditor key={q.id || i} q={q} testid={`quiz-q-${i}`}
            onChange={(nq) => set("quiz", f.quiz.map((x, xi) => (xi === i ? nq : x)))}
            onDelete={() => set("quiz", f.quiz.filter((_, xi) => xi !== i))} />
        ))}
      </div>

      <Button onClick={save} disabled={saving} className="bg-lomond text-black font-heading font-extrabold uppercase hover:bg-[#E6B400] w-full sm:w-auto" data-testid="module-save-btn">
        {saving ? "Saving..." : `Save Day ${m.day} Module`}
      </Button>
    </div>
  );
}

export default function TrainingContent() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [bank, setBank] = useState(null);
  const [selDay, setSelDay] = useState(1);
  const [ts, setTs] = useState({ final_theory_pass_mark: 80, final_theory_question_count: 55 });
  const [savingTs, setSavingTs] = useState(false);
  const [editingQ, setEditingQ] = useState(null);

  const load = () => {
    api.get("/admin/training/modules").then((r) => { setData(r.data); setTs(r.data.settings); }).catch(() => toast.error("Could not load training content"));
    api.get("/admin/training/final-bank").then((r) => setBank(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="h-8 w-8 border-4 border-steel border-t-lomond rounded-full animate-spin" data-testid="content-loading" />;
  const module = data.modules.find((m) => m.day === selDay);

  const saveSettings = async () => {
    setSavingTs(true);
    try {
      await api.put("/admin/training/settings", { final_theory_pass_mark: parseInt(ts.final_theory_pass_mark, 10), final_theory_question_count: parseInt(ts.final_theory_question_count, 10) });
      toast.success("Final assessment settings saved.");
    } catch (e) { toast.error(apiError(e)); } finally { setSavingTs(false); }
  };

  const saveBankQ = async (q) => {
    try {
      if (bank.questions.some((x) => x.id === q.id)) {
        await api.put(`/admin/training/final-bank/${q.id}`, q);
      } else {
        const { id, ...payload } = q;
        await api.post("/admin/training/final-bank", payload);
      }
      toast.success("Bank question saved.");
      setEditingQ(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="training-content-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => navigate("/staff/training")} className="border-steel text-charcoal bg-white" data-testid="back-to-camp-admin">
          <ArrowLeft className="h-4 w-4 mr-1" /> Training Camp
        </Button>
      </div>
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-lomond mb-1">Admin</p>
        <h1 className="font-heading font-extrabold uppercase tracking-tight text-charcoal text-2xl sm:text-3xl" data-testid="content-heading">Training Content</h1>
        <p className="text-sm text-slate-500 mt-1">Every module is editable — titles, objectives, lessons, media, safety messages, quizzes, practical exercises, instructor criteria and pass requirements.</p>
      </div>

      <div className="flex gap-1.5 flex-wrap" data-testid="module-tabs">
        {data.modules.map((m) => (
          <button key={m.day} type="button" onClick={() => setSelDay(m.day)}
            className={`px-3.5 py-2 rounded-md border text-xs font-bold uppercase tracking-wide transition-colors ${
              selDay === m.day ? "bg-lomond text-black border-lomond" : "bg-white border-steel text-slate-600 hover:border-lomond"
            }`} data-testid={`module-tab-${m.day}`}>
            Day {m.day}
          </button>
        ))}
      </div>

      {module && (
        <section className="bg-white border border-steel rounded-md p-5 sm:p-6">
          <ModuleEditor key={module.day} module={module} onSaved={load} />
        </section>
      )}

      <section className="bg-white border-2 border-lomond rounded-md p-5 sm:p-6" data-testid="final-settings-card">
        <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-base mb-4">Day 10 — Foundation Theory Assessment Settings</h2>
        <div className="grid sm:grid-cols-3 gap-3 items-end">
          <div className="space-y-1.5">
            <label className="text-sm text-slate-600">Pass mark %</label>
            <Input type="number" min="1" max="100" value={ts.final_theory_pass_mark} onChange={(e) => setTs({ ...ts, final_theory_pass_mark: e.target.value })} className={inputCls} data-testid="final-pass-mark" />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm text-slate-600">Questions drawn (bank {bank?.questions?.length ?? "…"})</label>
            <Input type="number" min="10" max="100" value={ts.final_theory_question_count} onChange={(e) => setTs({ ...ts, final_theory_question_count: e.target.value })} className={inputCls} data-testid="final-question-count" />
          </div>
          <Button onClick={saveSettings} disabled={savingTs} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="final-settings-save">Save Settings</Button>
        </div>
        <p className="text-xs text-slate-500 mt-3">Safety-critical rule: a trainee cannot pass on aggregate score alone — 1 safety-critical error triggers management review, 2+ fails the attempt regardless of score.</p>
      </section>

      <section className="bg-white border border-steel rounded-md p-5 sm:p-6" data-testid="final-bank-card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-base">Final Theory Question Bank ({bank?.questions?.length ?? 0})</h2>
          <Button size="sm" variant="outline" className="border-steel text-charcoal bg-white" data-testid="bank-add-btn"
            onClick={() => setEditingQ({ id: `new-${Date.now()}`, category: bank.categories[0], qtype: "multiple_choice", question: "", options: [], correct_answer: "", explanation: "", safety_critical: false, points: 1, active: true })}>
            <Plus className="h-4 w-4 mr-1" /> Add Bank Question
          </Button>
        </div>
        {editingQ && (
          <div className="mb-4 border-2 border-lomond rounded-md p-4">
            <QEditor q={editingQ} categories={bank.categories} testid="bank-editor"
              onChange={setEditingQ} onDelete={() => setEditingQ(null)} />
            <Button size="sm" onClick={() => saveBankQ(editingQ)} className="mt-3 bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="bank-save-btn">Save Question</Button>
          </div>
        )}
        <div className="space-y-2" data-testid="bank-list">
          {(bank?.questions || []).map((q) => (
            <div key={q.id} className="flex items-start justify-between gap-3 bg-elevated rounded-md p-3" data-testid={`bank-q-${q.id}`}>
              <div className="min-w-0">
                <p className="text-sm text-charcoal">{q.question}</p>
                <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-1">
                  {q.category} — {QTYPE_LABELS[q.qtype] || q.qtype}{q.safety_critical && <span className="text-red-600 ml-2">SAFETY-CRITICAL</span>}
                </p>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <Button size="sm" variant="outline" className="border-steel text-charcoal bg-white h-8 text-xs" onClick={() => setEditingQ(JSON.parse(JSON.stringify(q)))} data-testid={`bank-edit-${q.id}`}>Edit</Button>
                <Button size="sm" variant="outline" className="border-red-300 text-red-600 bg-white h-8 text-xs" data-testid={`bank-delete-${q.id}`}
                  onClick={async () => { if (!window.confirm("Delete this bank question?")) return; await api.delete(`/admin/training/final-bank/${q.id}`); toast.success("Deleted."); load(); }}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
